/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect } from "react";

import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from "@/components/ui/pagination";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";

interface PaginationComboProps {
    totalItems: number;
    perPageKey?: string;
    pageKey?: string;
}

const PaginationCombo: React.FC<PaginationComboProps> = ({ totalItems, perPageKey, pageKey }) => {
    const updateQueryParams = useUpdateQueryParams();
    const [searchParams] = useSearchParams();
    const pages = Math.ceil(totalItems / Number(searchParams.get(perPageKey || "per_page") || 10));

    const handleRowsChange = (value: string) => {
        const paramsObj: any = {};
        paramsObj[perPageKey || "per_page"] = value;
        updateQueryParams(paramsObj);
    };

    const handlePageClick = (page: number) => {
        const paramsObj: any = {};
        paramsObj[pageKey || "page"] = page;
        updateQueryParams(paramsObj);
    };

    const handleNextClick = () => {
        const currentPage = Number(searchParams.get(pageKey || "page"));
        if (currentPage === pages) return;
        const paramsObj: any = {};
        paramsObj[pageKey || "page"] = currentPage + 1;
        updateQueryParams(paramsObj);
    };

    const handlePreviousClick = () => {
        const currentPage = Number(searchParams.get(pageKey || "page"));
        if (currentPage === 1) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const paramsObj: any = {};
        paramsObj[pageKey || "page"] = currentPage - 1;
        updateQueryParams(paramsObj);
    };

    function getPaginationData(
        totalPages: number,
        activePage: number
    ): {
        hasPrevious: boolean;
        hasAfter: boolean;
        pagesArray: number[];
    } {
        const maxVisiblePages = 5;

        // Default values for pagination bounds
        let startPage = Math.max(1, activePage - Math.floor(maxVisiblePages / 2));
        let endPage = startPage + maxVisiblePages - 1;

        // Adjust if endPage exceeds totalPages
        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const hasPrevious = startPage > 1;
        const hasAfter = endPage < totalPages;

        const pagesArray = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

        return {
            hasPrevious,
            hasAfter,
            pagesArray,
        };
    }

    useEffect(() => {
        if (!Number(searchParams.get(perPageKey || "per_page"))) {
            updateQueryParams({ per_page: "10" });
        }

        if (!Number(searchParams.get(pageKey || "page"))) {
            updateQueryParams({ page: "1" });
        }
    }, [searchParams, updateQueryParams, pageKey, perPageKey]);

    return (
        <div className="flex w-full justify-between border-t p-2">
            <Select onValueChange={(value) => handleRowsChange(value)} value={searchParams.get(perPageKey || "per_page") || "10"}>
                <SelectTrigger className="w-[120px] md:w-[180px]">
                    <SelectValue placeholder="Rows Per Page" />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectLabel>Per Page</SelectLabel>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
            <Pagination className="mx-0 w-fit">
                <PaginationContent>
                    <PaginationItem className="cursor-pointer" onClick={() => handlePreviousClick()}>
                        <p className="flex items-center gap-1 text-sm">
                            <ChevronLeft className="size-4" />
                        </p>
                    </PaginationItem>
                    {getPaginationData(pages, Number(searchParams.get(pageKey || "page") || "1")).hasPrevious && (
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                    )}
                    {getPaginationData(pages, Number(searchParams.get(pageKey || "page") || "1")).pagesArray.map((page) => (
                        <PaginationItem className="cursor-pointer p-2" key={page} onClick={() => handlePageClick(page)}>
                            {page}
                        </PaginationItem>
                    ))}
                    {getPaginationData(pages, Number(searchParams.get(pageKey || "page") || "1")).hasAfter && (
                        <PaginationItem>
                            <PaginationEllipsis />
                        </PaginationItem>
                    )}
                    <PaginationItem className="cursor-pointer" onClick={() => handleNextClick()}>
                        <p className="flex items-center gap-1 text-sm">
                            <ChevronRight className="size-4" />
                        </p>
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
};

export default PaginationCombo;
